import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Tabs,
  Tab,
  InputBase,
  Avatar,
  Typography,
  Button,
  Paper,
  Stack,
  Pagination,
  TextField,
  FormControl,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";

import InsertLinkIcon from "@mui/icons-material/InsertLink";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useQuery, useMutation } from "react-query";
import { useSelector } from "react-redux";
import files from "src/utils/constants";
import { debounce } from "lodash";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";
import TeamMemberLinkingFormDialog from "./TeamMemberLinkingFormDialog";

// TAB_CONFIG will be created inside component to access t()

const REQUEST_STATUS = {
  PENDING: 0,
  ACCEPTED: 1,
  REJECTED: 2
};

function TeamMemberLinkingContent({ isEmbedded = false, onEditItem }) {
  const { t } = useTranslation("dashboard");
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const business = useSelector((state) => state.business.business);
  const primaryLocationId = useSelector((state) => state.auth.primaryLocationId);
  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);
  const { isViewOnly, hasPermission } = useBusinessPermissions();
  const canManageTeam = hasPermission("manageTeamMembers");

  // Create TAB_CONFIG inside component to access t()
  const TAB_CONFIG = [
    { 
      key: "linked",
      label: t("teamMemberLinkingTabLinked"), 
      endpoint: ApiEndPoints.TEAM_LINKING.get,
      primaryAction: t("teamMemberLinkingAction"),
      secondaryAction: t("teamMemberLinkingOption")
    },
    { 
      key: "followers",
      label: t("teamMemberLinkingTabFollowers"), 
      endpoint: (businessId) => ApiEndPoints.TEAM_LINKING.get_followers(businessId),
      primaryAction: t("teamMemberLinkingFollowBack"),
      secondaryAction: t("teamMemberLinkingBlock"),
      requiresBusiness: true
    },
    { 
      key: "beɘmz users",
      label: t("teamMemberLinkingTabBeemzUsers"), 
      endpoint: ApiEndPoints.TEAM_LINKING.getBemmzUser,
      primaryAction: t("teamMemberLinkingConnect"),
      secondaryAction: t("teamMemberLinkingIgnore")
    },
  ];

  // Form validation schema (needs t() for error messages)
  const invitationSchema = yup.object({
    name: yup
      .string()
      .required(t("teamMemberLinkingNameRequired"))
      .min(2, t("teamMemberLinkingNameMinLength"))
      .max(50, t("teamMemberLinkingNameMaxLength")),
    email: yup
      .string()
      .required(t("teamMemberLinkingEmailRequired"))
      .email(t("teamMemberLinkingEmailInvalid")),
  });

  const activeTabConfig = TAB_CONFIG[tab];

  // Send invitation mutation
  const { mutate: sendInvitation, isLoading: isSendingInvitation } = useMutation({
    mutationFn: async (invitationData) => {
      const res = await axiosInstance.post(
        ApiEndPoints.TEAM_LINKING.send_team_invitation,
        invitationData
      );
      return res.data;
    },
    onSuccess: (res) => {
      if (res.status === "success") {
        toast.success(res.message || t("teamMemberLinkingInvitationSent"));
        formik.resetForm();
        setInviteDialogOpen(false);
      } else {
        toast.error(res.message || t("teamMemberLinkingInvitationFailed"));
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("teamMemberLinkingSomethingWentWrong"));
    },
  });

  // Form for invitation
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
    },
    validationSchema: invitationSchema,
    onSubmit: (values) => {
      sendInvitation(values);
    },
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setCurrentPage(1);
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
    formik.resetForm();
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchQuery(value);
      setCurrentPage(1);
    }, 800),
    []
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    
    if (value === '' || value.length >= 2) {
      debouncedSearch(value);
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Generic query builder
  const buildQueryUrl = useCallback((config) => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery.trim()) {
      params.append('search', debouncedSearchQuery.trim());
    }
    params.append('page', currentPage.toString());
    params.append('limit', pageSize.toString());
    
    // Add locationId as query parameter if available (for linked and beɘmz users tabs)
    if (primaryLocationId && (config.key === "linked" || config.key === "beɘmz users")) {
      params.append('locationId', primaryLocationId);
    }

    // Apply filterReqStatus=true for beɘmz users ONLY if there is no search query
    // This allows the initial "filtered" view, but global search when user types
    if (config.key === "beɘmz users" && !debouncedSearchQuery.trim()) {
      params.append('filterReqStatus', 'true');
    }

    if (config.queryParams) {
      Object.entries(config.queryParams).forEach(([key, value]) => {
        params.append(key, value);
      });
    }
    
    const queryString = params.toString();
    let baseUrl = config.endpoint;
    
    if (config.requiresBusiness) {
      baseUrl = config.endpoint(selectedBusinessId);
    } else if (config.requiresLocation) {
      baseUrl = config.endpoint(primaryLocationId);
    }
    
    return `${baseUrl}${queryString ? `?${queryString}` : ''}`;
  }, [debouncedSearchQuery, currentPage, pageSize, primaryLocationId, selectedBusinessId]);

  // Generic data fetcher
  const fetchTabData = async ({ signal }, config) => {
    if (config.requiresBusiness && !selectedBusinessId) {
      throw new Error("business id not available");
    }
    if (config.requiresLocation && !primaryLocationId) {
      throw new Error("primary location id not available");
    }
    
    const url = buildQueryUrl(config);
    const res = await axiosInstance.get(url, { signal });
    return res.data.data.data || { data: [], pagination: {} };
  };

  // Single query hook for active tab
  const { data: tabData, isLoading, error } = useQuery(
    [`tab-data-${tab}`, activeTabConfig.label, debouncedSearchQuery, currentPage, pageSize, primaryLocationId, selectedBusinessId],
    ({ signal }) => fetchTabData({ signal }, activeTabConfig),
    {
      refetchOnWindowFocus: false,
      enabled: (!activeTabConfig.requiresBusiness || !!selectedBusinessId) && (!activeTabConfig.requiresLocation || !!primaryLocationId),
      retry: 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      onError: (error) => {
        console.error(`error fetching ${activeTabConfig.label}:`, error);
      },
    }
  );

  // Button renderer with memoization
  const renderActionButton = useCallback((item, tabTypeLabel) => {
    // Enable if manageTeamMembers is true (even if viewOnly is true)
    const isDisabled = isViewOnly() && !canManageTeam;
    const activeTabKey = TAB_CONFIG.find(tab => tab.label === tabTypeLabel)?.key;

    const commonButtonProps = {
      variant: "outlined",
      disabled: isDisabled,
      sx: {
        minWidth: 0,
        px: isEmbedded ? { xs: 1, sm: 1.5 } : 1.5,
        py: isEmbedded ? { xs: 0.75, sm: 1 } : 1,
        borderRadius: 2,
        bgcolor: isDisabled ? "#E0E0E0" : "#8800F7",
        borderColor: isDisabled ? "#E0E0E0" : "#8800F7",
        color: isDisabled ? "#757575" : "#8800F7",
        cursor: isDisabled ? "not-allowed" : "pointer",
        flexShrink: 0,
        "&:hover": {
          bgcolor: isDisabled ? "#E0E0E0" : "#f3e8ff",
          borderColor: isDisabled ? "#E0E0E0" : "#6C00C5",
          color: isDisabled ? "#757575" : "#6C00C5",
        },
      },
      onClick: () => {
        if (!isDisabled) {
          // If onEditItem callback is provided (embedded mode), use it
          // Otherwise, open dialog (standalone mode)
          if (onEditItem) {
            onEditItem(item);
          } else {
            setSelectedItem(item);
            setFormDialogOpen(true);
          }
        }
      },
    };

    const statusButtonProps = (bgColor, borderColor, textColor, text) => ({
      variant: "outlined",
      disabled: true,
      sx: {
        minWidth: 0,
        px: isEmbedded ? { xs: 2, sm: 2.5 } : 2.5,
        py: 0.5,
        borderRadius: 2,
        bgcolor: bgColor,
        borderColor: borderColor,
        color: textColor,
        cursor: "not-allowed",
        fontWeight: 700,
        fontSize: isEmbedded ? { xs: 14, sm: 15, md: 16 } : 16,
        textTransform: "none",
        flexShrink: 0,
        whiteSpace: "nowrap",
        "&:hover": {
          bgcolor: bgColor,
          borderColor: borderColor,
          color: textColor,
        },
      },
      children: text
    });

    // Handle linked users (employees don't show buttons)
    if (activeTabKey === "linked" && item.isEmployee) {
      return null;
    }

    // Handle request status
    switch (item.requestStatus) {
      case REQUEST_STATUS.PENDING:
        return <Button {...statusButtonProps("#FFF3E0", "#FF9800", "#FF9800", t("teamMemberLinkingRequested"))} />;
      
      case REQUEST_STATUS.ACCEPTED:
        return <Button {...statusButtonProps("#E0E0E0", "#E0E0E0", "#757575", t("teamMemberLinkingAdded"))} />;
      
      case REQUEST_STATUS.REJECTED:
        return <Button {...statusButtonProps("#FFEBEE", "#F44336", "#F44336", t("teamMemberLinkingRejected"))} />;
      
      default:
        // No status or null - show action button
        if (activeTabKey === "contacts") {
          return (
            <Stack direction="row" spacing={1}>
              <Button 
                variant="contained" 
                disabled={isViewOnly() && !canManageTeam}
                sx={{
                  ...contactButtonStyle,
                  ...(isViewOnly() && !canManageTeam ? {
                    bgcolor: "#E0E0E0",
                    color: "#757575",
                    cursor: "not-allowed",
                    "&:hover": { bgcolor: "#E0E0E0" },
                  } : {}),
                }}
              >
                {activeTabConfig.primaryAction}
              </Button>
              <Button 
                variant="contained" 
                disabled={isViewOnly() && !canManageTeam}
                sx={{
                  ...contactButtonStyle,
                  ...(isViewOnly() && !canManageTeam ? {
                    bgcolor: "#E0E0E0",
                    color: "#757575",
                    cursor: "not-allowed",
                    "&:hover": { bgcolor: "#E0E0E0" },
                  } : {}),
                }}
              >
                {activeTabConfig.secondaryAction}
              </Button>
            </Stack>
          );
        }
        return (
          <Button {...commonButtonProps}>
            <InsertLinkIcon sx={{ 
              color: isViewOnly() && !canManageTeam ? "#757575" : "white", 
              fontSize: isEmbedded ? { xs: 18, sm: 20, md: 22 } : 22,
              "&:hover": { color: isViewOnly() && !canManageTeam ? "#757575" : "#6C00C5" } 
            }} />
          </Button>
        );
    }
  }, [activeTabConfig, isViewOnly, canManageTeam, onEditItem]);

  const contactButtonStyle = {
    bgcolor: "#8800F7",
    color: "#fff",
    fontWeight: 700,
    fontSize: 16,
    px: 2.5,
    py: 0.5,
    borderRadius: 2,
    boxShadow: "none",
    textTransform: "none",
    minWidth: 0,
    "&:hover": { bgcolor: "#6C00C5" },
  };

  // User card renderer
  const renderUserCard = (dataToDisplay = []) => {
    if (!Array.isArray(dataToDisplay) || dataToDisplay.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Typography color="text.secondary">{t("teamMemberLinkingNoDataAvailable")}</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} width="100%">
        {dataToDisplay.map((item, index) => (
          <Box
            key={item._id || index}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{
              p: isEmbedded ? { xs: 1.5, sm: 2 } : 2,
              borderRadius: 2,
              "&:hover": { bgcolor: "#f8f9fa" },
              minWidth: 0, // Allow shrinking
            }}
          >
            <Box 
              display="flex" 
              alignItems="center" 
              flex={1}
              minWidth={0} // Allow text truncation
              sx={{ overflow: "hidden" }}
            >
              <Avatar
                src={files(item?.image?.profilePic || item?.profilePic, "image")}
                sx={{ 
                  width: isEmbedded ? { xs: 48, sm: 50, md: 54 } : 54, 
                  height: isEmbedded ? { xs: 48, sm: 50, md: 54 } : 54, 
                  mr: { xs: 1.5, sm: 2 },
                  flexShrink: 0,
                }}
                alt={item.fullName}
              />
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: isEmbedded ? { xs: 16, sm: 18, md: 20 } : 20, 
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.fullName}
                </Typography>
                <Typography 
                  sx={{ 
                    color: "#B1B8C8", 
                    fontSize: isEmbedded ? { xs: 14, sm: 15, md: 16 } : 16, 
                    mt: 0.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  @{item?.userName}
                </Typography>
              </Box>
            </Box>
            {renderActionButton(item, activeTabConfig.label)}
          </Box>
        ))}
      </Stack>
    );
  };

  // Tab content renderer
  const renderTabContent = () => {
    const dataArray = tabData?.data || [];
    const count = dataArray.length;
    
    // Special handling for beɘmz users tab
    if (activeTabConfig.key === "beɘmz users") {
    return (
      <Box mt={2} sx={{ width: "100%" }}>
        <Typography 
          variant="h6" 
          fontWeight={600}
          sx={{
            fontSize: isEmbedded ? { xs: 16, sm: 18, md: 20 } : 20,
          }}
        >
          {activeTabConfig.label} {debouncedSearchQuery.trim() && count > 0 && `(${count})`}
        </Typography>
          <Box mt={1}>
            {!debouncedSearchQuery.trim() && dataArray.length === 0 && !isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Typography color="text.secondary" textAlign="center">
                  {t("teamMemberLinkingSearchToFindBeemzUsers")}
                </Typography>
              </Box>
            ) : isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <CircularProgress size={40} sx={{ color: "#6200EE", mb: 2 }} />
                <Typography color="text.secondary">
                  {t("teamMemberLinkingSearchingForBeemzUsers")}
                </Typography>
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Typography color="error">
                  {t("teamMemberLinkingErrorLoading", { label: activeTabConfig.label })}: {error.message}
                </Typography>
              </Box>
            ) : (
              <>
                {renderUserCard(dataArray)}
                {tabData?.pagination?.pages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={tabData.pagination.pages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      );
    }
    
    return (
      <Box mt={2} sx={{ width: "100%" }}>
        <Typography 
          variant="h6" 
          fontWeight={600}
          sx={{
            fontSize: isEmbedded ? { xs: 16, sm: 18, md: 20 } : 20,
          }}
        >
          {activeTabConfig.label} {count > 0 && `(${count})`}
        </Typography>
        <Box mt={1}>
          {((!selectedBusinessId && activeTabConfig.requiresBusiness) || (!primaryLocationId && activeTabConfig.requiresLocation)) ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>{t("teamMemberLinkingLoadingBusinessData")}</Typography>
            </Box>
          ) : isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>{t("teamMemberLinkingLoading", { label: activeTabConfig.label })}</Typography>
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography color="error">
                {t("teamMemberLinkingErrorLoading", { label: activeTabConfig.label })}: {error.message}
              </Typography>
            </Box>
          ) : (
            <>
              {renderUserCard(dataArray)}
              {tabData?.pagination?.pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={tabData.pagination.pages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const searchPlaceholder = useMemo(() => 
    t("teamMemberLinkingSearchPlaceholder", { label: activeTabConfig.label }),
    [activeTabConfig.label, t]
  );

  return (
    <Box 
      sx={{ 
        minHeight: isEmbedded ? "auto" : "100vh", 
        width: "100%",
        maxWidth: isEmbedded ? { xs: "100%", sm: "800px", md: "900px", lg: "1000px" } : "100%",
        mx: "auto",
        boxSizing: "border-box",
      }}
    >
      <Typography 
        fontWeight={600} 
        lineHeight={3} 
        variant="h6"
        sx={{
          fontSize: isEmbedded ? { xs: 18, sm: 20, md: 22 } : 20,
          mb: isEmbedded ? 2 : 0,
        }}
      >
        {t("teamMemberLinkingTitle")}
      </Typography>
      
      {/* Tabs with + icon */}
      <Box 
        display="flex" 
        alignItems="center" 
        gap={{ xs: 1, sm: 2 }} 
        mb={3} 
        mt={isEmbedded ? 0 : 2}
        sx={{
          width: "100%",
          minWidth: 0, // Prevent overflow
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 7,
            bgcolor: "#F6F6F6",
            p: 0.5,
            flex: 1,
            minWidth: 0, // Allow shrinking
            overflow: "hidden", // Prevent content from overflowing
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: 0,
              "& .MuiTabs-scrollButtons": {
                width: isEmbedded ? 24 : 32,
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
              "& .MuiTab-root": {
                minWidth: isEmbedded ? { xs: 80, sm: 100, md: 120 } : 120,
                minHeight: isEmbedded ? { xs: 32, sm: 36, md: 38 } : 38,
                px: isEmbedded ? { xs: 1.5, sm: 2 } : 2,
                borderRadius: 5,
                mx: 0.5,
                bgcolor: "#F6F6F6",
                color: "#222",
                fontWeight: 400,
                fontSize: isEmbedded ? { xs: 14, sm: 15, md: 17 } : 17,
                transition: "all .2s",
                textTransform: "lowercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                "&.Mui-selected": {
                  bgcolor: "#fff",
                  color: "#8800F7",
                  fontWeight: 700,
                  boxShadow: "0 2px 6px #eee",
                },
              },
            }}
          >
            {TAB_CONFIG.map((item) => (
              <Tab key={item.label} label={item.label} />
            ))}
          </Tabs>
        </Paper>
        
        {/* + Icon Button - Disabled if both viewOnly is true AND manageTeamMembers is false */}
        <IconButton
          onClick={() => {
            if (canManageTeam) {
              setInviteDialogOpen(true);
            }
          }}
          disabled={isViewOnly() && !canManageTeam}
          sx={{
            bgcolor: isViewOnly() && !canManageTeam ? "#E0E0E0" : "#8800F7",
            color: isViewOnly() && !canManageTeam ? "#757575" : "white",
            width: isEmbedded ? { xs: 32, sm: 36, md: 38 } : 38,
            height: isEmbedded ? { xs: 32, sm: 36, md: 38 } : 38,
            flexShrink: 0, // Prevent button from shrinking
            cursor: isViewOnly() && !canManageTeam ? "not-allowed" : "pointer",
            "&:hover": {
              bgcolor: isViewOnly() && !canManageTeam ? "#E0E0E0" : "#6C00C5",
            },
            "&:disabled": {
              bgcolor: "#E0E0E0",
              color: "#757575",
            },
          }}
        >
          <AddIcon sx={{ fontSize: isEmbedded ? { xs: 18, sm: 20, md: 22 } : 22 }} />
        </IconButton>
      </Box>

      {/* Search */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          px: isEmbedded ? { xs: 1.5, sm: 2 } : 2,
          py: isEmbedded ? { xs: 0.75, sm: 1 } : 1,
          mb: 2.5,
          borderRadius: 2,
          border: "1px solid #D3D3D3",
          bgcolor: "#fff",
          width: "100%",
        }}
      >
        <InputBase
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: "100%",
            fontSize: isEmbedded ? { xs: 16, sm: 17, md: 18 } : 18,
            color: "#444",
            letterSpacing: 0.5,
          }}
          inputProps={{ "aria-label": "search" }}
        />
        {isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {debouncedSearchQuery.length > 0 ? t("teamMemberLinkingSearching") : t("teamMemberLinkingLoadingGeneric")}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Tab Content */}
      <Box>{renderTabContent()}</Box>

      {/* Invite Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleInviteDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>
            {t("teamMemberLinkingSendInvitationTitle")}
          </Typography>
          <IconButton onClick={handleInviteDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <form onSubmit={formik.handleSubmit} id="invitation-form">
            <Stack spacing={3}>
              <FormControl fullWidth error={formik.touched.name && Boolean(formik.errors.name)}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label={t("teamMemberLinkingFullName")}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder={t("teamMemberLinkingEnterFullName")}
                  sx={{
                    "& .MuiInputLabel-root": {
                      textTransform: "lowercase",
                    },
                    "& .MuiFormHelperText-root": {
                      textTransform: "lowercase",
                    },
                  }}
                />
              </FormControl>

              <FormControl fullWidth error={formik.touched.email && Boolean(formik.errors.email)}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label={t("teamMemberLinkingEmailAddress")}
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  placeholder={t("teamMemberLinkingEnterEmailAddress")}
                  sx={{
                    "& .MuiInputLabel-root": {
                      textTransform: "lowercase",
                    },
                    "& .MuiFormHelperText-root": {
                      textTransform: "lowercase",
                    },
                  }}
                />
              </FormControl>
            </Stack>
          </form>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            variant="contained"
            onClick={handleInviteDialogClose}
             sx={{
              bgcolor: "#6200EE",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              py: 1,
              px: 3,
              borderRadius: 2,
              boxShadow: "none",
              textTransform: "lowercase",
              "&:hover": { bgcolor: "#6C00C5" },
              "&:disabled": {
                bgcolor: "#E0E0E0",
                color: "#757575",
              },
            }}
          >
            {t("teamMemberLinkingCancel")}
          </Button>
          <Button
            type="submit"
            form="invitation-form"
            variant="contained"
            disabled={isSendingInvitation}
            sx={{
              bgcolor: "#6200EE",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              py: 1,
              px: 3,
              borderRadius: 2,
              boxShadow: "none",
              textTransform: "lowercase",
              "&:hover": { bgcolor: "#6C00C5" },
              "&:disabled": {
                bgcolor: "#E0E0E0",
                color: "#757575",
              },
            }}
          >
            {isSendingInvitation ? t("teamMemberLinkingSending") : t("teamMemberLinkingSendInvitation")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Member Linking Form Dialog */}
      <TeamMemberLinkingFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedItem(null);
        }}
        initialData={selectedItem}
      />
    </Box>
  );
}

export default TeamMemberLinkingContent;
